import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { adminAPI } from '../lib/api';
import { toast } from 'sonner';
import {
  LayoutDashboard,
  Users,
  Search,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronLeft,
  TrendingUp,
  UserPlus,
  Eye,
  Download,
  Settings,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper component for profile avatar with photo support
const ProfileAvatar = ({ marketer, size = 'md', showRank = false, rank = 0 }) => {
  const [imageError, setImageError] = useState(false);
  
  const sizeClasses = {
    sm: 'w-10 h-10',
    md: 'w-10 h-10 sm:w-12 sm:h-12',
  };
  
  const textSizes = {
    sm: 'text-base',
    md: 'text-base sm:text-lg',
  };

  const imageUrl = marketer?.profile_image_url 
    ? (marketer.profile_image_url.startsWith('/api') 
        ? `${BACKEND_URL}${marketer.profile_image_url}` 
        : marketer.profile_image_url)
    : null;

  if (showRank) {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0`}>
        <span className="font-bold text-sky-700">#{rank}</span>
      </div>
    );
  }

  // Show fallback if no image URL or if image failed to load
  if (!imageUrl || imageError) {
    return (
      <div className={`${sizeClasses[size]} rounded-full bg-primary-200 flex items-center justify-center flex-shrink-0`}>
        <span className={`font-semibold text-primary-800 ${textSizes[size]}`}>
          {marketer?.full_name?.charAt(0).toUpperCase() || '?'}
        </span>
      </div>
    );
  }

  return (
    <img 
      src={imageUrl}
      alt={marketer?.full_name || 'Profile'}
      className={`${sizeClasses[size]} rounded-full object-cover flex-shrink-0 border-2 border-primary-200`}
      onError={() => setImageError(true)}
    />
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [marketers, setMarketers] = useState([]);
  const [totalMarketersCount, setTotalMarketersCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 50;

  useEffect(() => {
    fetchDashboard();
    fetchMarketers();
  }, []);

  const fetchDashboard = async () => {
    try {
      const response = await adminAPI.getDashboard();
      setDashboardData(response.data);
    } catch (error) {
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarketers = async (search = '', page = 1) => {
    setSearching(true);
    try {
      const skip = (page - 1) * PAGE_SIZE;
      const response = await adminAPI.getMarketers({ search, skip, limit: PAGE_SIZE });
      setMarketers(response.data.marketers);
      setTotalMarketersCount(response.data.total);
      setCurrentPage(page);
    } catch (error) {
      toast.error('Failed to load marketers');
    } finally {
      setSearching(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchMarketers(searchQuery, 1);
  };

  const handlePageChange = (newPage) => {
    fetchMarketers(searchQuery, newPage);
  };

  const totalPages = Math.ceil(totalMarketersCount / PAGE_SIZE);

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/export/marketers`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `medichi_tribe_marketers_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('CSV exported successfully!');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const { stats, recent_marketers, top_recruiters } = dashboardData || {};

  return (
    <div className="flex h-screen overflow-hidden bg-neutral-background" data-testid="admin-dashboard">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed md:relative z-50 w-64 h-full flex flex-col sidebar-dark transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        }`}
      >
        <div className="p-6 border-b border-primary-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-primary-700 flex items-center justify-center">
                <span className="text-white font-serif text-xl font-semibold">M</span>
              </div>
              <div>
                <span className="font-serif text-xl text-white block">Medichi Tribe</span>
                <span className="text-xs text-primary-400">Admin Portal</span>
              </div>
            </div>
            <button
              onClick={() => setSidebarOpen(false)}
              className="md:hidden p-2 rounded-lg text-primary-200 hover:bg-primary-800 hover:text-white"
              data-testid="close-sidebar-btn"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <nav className="flex-1 p-4">
          <div className="space-y-1">
            <button
              onClick={() => { setActiveTab('dashboard'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                activeTab === 'dashboard'
                  ? 'bg-primary-800 text-white'
                  : 'text-primary-200 hover:bg-primary-800 hover:text-white'
              }`}
              data-testid="nav-dashboard"
            >
              <LayoutDashboard className="h-5 w-5" />
              Dashboard
            </button>
            <button
              onClick={() => { setActiveTab('marketers'); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl transition-colors ${
                activeTab === 'marketers'
                  ? 'bg-primary-800 text-white'
                  : 'text-primary-200 hover:bg-primary-800 hover:text-white'
              }`}
              data-testid="nav-marketers"
            >
              <Users className="h-5 w-5" />
              Marketers
            </button>
            <button
              onClick={() => { navigate('/admin/teams-and-training'); setSidebarOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-primary-200 hover:bg-primary-800 hover:text-white transition-colors"
              data-testid="nav-teams-training"
            >
              <Users className="h-5 w-5" />
              Teams &amp; Training
            </button>
            <button
              onClick={() => { navigate('/admin/settings'); setSidebarOpen(false); }}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-primary-200 hover:bg-primary-800 hover:text-white transition-colors"
              data-testid="nav-settings"
            >
              <Settings className="h-5 w-5" />
              Settings
            </button>
          </div>
        </nav>

        <div className="p-4 border-t border-primary-800">
          <div className="px-4 py-3 mb-3">
            <p className="text-primary-300 text-sm">Admin</p>
            <p className="text-white font-medium truncate">{user?.full_name}</p>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-primary-200 hover:bg-primary-800 hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="h-5 w-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        {/* Header */}
        <header className="sticky top-0 z-30 bg-white/80 backdrop-blur-lg border-b border-neutral-border px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 rounded-lg hover:bg-neutral-100 flex-shrink-0"
                data-testid="mobile-menu-btn"
              >
                <Menu className="h-6 w-6" />
              </button>
              <h1 className="font-heading text-lg sm:text-2xl font-semibold text-primary-900 truncate">
                {activeTab === 'dashboard' ? 'Dashboard' : 'Marketers'}
              </h1>
            </div>
            <Button
              onClick={handleExportCSV}
              disabled={exporting}
              className="bg-primary-900 hover:bg-primary-800 text-white rounded-xl text-xs sm:text-sm px-3 sm:px-4 flex-shrink-0"
              data-testid="export-csv-btn"
            >
              <Download className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">{exporting ? 'Exporting...' : 'Export CSV'}</span>
            </Button>
          </div>
        </header>

        <div className="p-6 lg:p-8 space-y-8">
          {activeTab === 'dashboard' ? (
            <>
              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="border-neutral-border shadow-card" data-testid="stat-total">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-neutral-text-muted mb-1">Total Marketers</p>
                        <p className="text-3xl font-bold text-primary-900">{stats?.total_marketers || 0}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center">
                        <Users className="h-6 w-6 text-primary-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-neutral-border shadow-card" data-testid="stat-recruiters">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-neutral-text-muted mb-1">Active Recruiters</p>
                        <p className="text-3xl font-bold text-accent-600">{stats?.marketers_with_recruits || 0}</p>
                      </div>
                      <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center">
                        <TrendingUp className="h-6 w-6 text-accent-600" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Top Recruiters */}
              <Card className="border-neutral-border shadow-card" data-testid="top-recruiters-card">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-primary-900">Top Recruiters</CardTitle>
                  <CardDescription>Marketers with the most recruits</CardDescription>
                </CardHeader>
                <CardContent>
                  {top_recruiters && top_recruiters.length > 0 ? (
                    <div className="space-y-3">
                      {top_recruiters.map((recruiter, index) => (
                        <Link
                          key={recruiter.id}
                          to={`/admin/marketers/${recruiter.id}`}
                          className="block p-3 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors group"
                          data-testid={`top-recruiter-${index}`}
                        >
                          <div className="flex items-center gap-3">
                            {/* Rank badge */}
                            <div className="w-8 h-8 rounded-full bg-sky-100 flex items-center justify-center flex-shrink-0">
                              <span className="font-bold text-sky-700 text-sm">#{index + 1}</span>
                            </div>
                            {/* Profile photo with fallback */}
                            <ProfileAvatar marketer={recruiter} size="sm" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-primary-900 truncate">{recruiter.full_name}</p>
                              <p className="text-xs text-neutral-text-muted truncate">{recruiter.location}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge className="bg-primary-100 text-primary-700 text-xs">
                                {recruiter.recruits_count} recruits
                              </Badge>
                              <ChevronRight className="h-4 w-4 text-neutral-400 group-hover:text-primary-600 hidden sm:block" />
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-neutral-text-muted py-8">No recruiters yet</p>
                  )}
                </CardContent>
              </Card>

              {/* Recent Marketers */}
              <Card className="border-neutral-border shadow-card" data-testid="recent-marketers-card">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-primary-900">Recent Registrations</CardTitle>
                  <CardDescription>Newly joined marketers</CardDescription>
                </CardHeader>
                <CardContent>
                  {recent_marketers && recent_marketers.length > 0 ? (
                    <div className="space-y-3">
                      {recent_marketers.slice(0, 5).map((marketer, index) => (
                        <Link
                          key={marketer.id}
                          to={`/admin/marketers/${marketer.id}`}
                          className="block p-3 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors group"
                          data-testid={`recent-marketer-${index}`}
                        >
                          <div className="flex items-center gap-3">
                            {/* Profile photo with fallback */}
                            <ProfileAvatar marketer={marketer} size="sm" />
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-primary-900 truncate">{marketer.full_name}</p>
                              <p className="text-xs text-neutral-text-muted truncate">{marketer.email}</p>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              <Badge variant="secondary" className="text-xs">
                                {new Date(marketer.created_at).toLocaleDateString()}
                              </Badge>
                              <ChevronRight className="h-4 w-4 text-neutral-400 group-hover:text-primary-600 hidden sm:block" />
                            </div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-neutral-text-muted py-8">No marketers yet</p>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Search */}
              <Card className="border-neutral-border shadow-card" data-testid="search-card">
                <CardContent className="p-6">
                  <form onSubmit={handleSearch} className="flex gap-4">
                    <div className="relative flex-1">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                      <Input
                        placeholder="Search by name, email, phone, or location..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="h-11 pl-10 rounded-xl"
                        data-testid="search-input"
                      />
                    </div>
                    <Button
                      type="submit"
                      disabled={searching}
                      className="h-11 px-6 rounded-xl bg-primary-900 hover:bg-primary-800"
                      data-testid="search-btn"
                    >
                      {searching ? 'Searching...' : 'Search'}
                    </Button>
                  </form>
                </CardContent>
              </Card>

              {/* Marketers List */}
              <Card className="border-neutral-border shadow-card" data-testid="marketers-list-card">
                <CardHeader>
                  <CardTitle className="font-heading text-xl text-primary-900">
                    All Marketers ({totalMarketersCount})
                  </CardTitle>
                  <CardDescription>
                    Showing {marketers.length} of {totalMarketersCount} marketers
                    {totalPages > 1 && ` • Page ${currentPage} of ${totalPages}`}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {marketers.length > 0 ? (
                    <div className="space-y-4">
                      {marketers.map((marketer, index) => (
                        <Link
                          key={marketer.id}
                          to={`/admin/marketers/${marketer.id}`}
                          className="block p-4 rounded-xl bg-neutral-50 hover:bg-neutral-100 transition-colors group"
                          data-testid={`marketer-${index}`}
                        >
                          <div className="flex items-start gap-3">
                            {/* Profile photo with fallback */}
                            <ProfileAvatar marketer={marketer} size="md" />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-primary-900 truncate">{marketer.full_name}</p>
                                  <p className="text-sm text-neutral-text-muted truncate">{marketer.email}</p>
                                </div>
                                <ChevronRight className="h-5 w-5 text-neutral-400 group-hover:text-primary-600 flex-shrink-0 mt-1" />
                              </div>
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                <Badge className="bg-primary-100 text-primary-700 text-xs">
                                  {marketer.recruits_count || 0} recruits
                                </Badge>
                                <span className="text-xs text-neutral-400">
                                  {marketer.location}
                                </span>
                                <span className="text-xs text-neutral-400">
                                  • Joined {new Date(marketer.created_at).toLocaleDateString()}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}
                      
                      {/* Pagination Controls */}
                      {totalPages > 1 && (
                        <div className="flex items-center justify-between pt-4 border-t border-neutral-200">
                          <Button
                            variant="outline"
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || searching}
                            className="rounded-xl"
                            data-testid="prev-page-btn"
                          >
                            <ChevronLeft className="h-4 w-4 mr-1" />
                            Previous
                          </Button>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-neutral-text-muted">
                              Page {currentPage} of {totalPages}
                            </span>
                          </div>
                          <Button
                            variant="outline"
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || searching}
                            className="rounded-xl"
                            data-testid="next-page-btn"
                          >
                            Next
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <p className="text-center text-neutral-text-muted py-12">
                      {searchQuery ? 'No marketers found matching your search' : 'No marketers registered yet'}
                    </p>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
